import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  getAskingPrice,
  mapValueToEbitdaFormat,
  mapValueToMoneyFormat,
} from 'utils';
import {
  ASKING_PRICE_SELECT,
  AskingPriceSelect,
  Currency,
  STOCK_AT_VALUE,
  StockAtValue,
} from 'models/DealRoom';
import { RequiredVisibleInfo } from 'models/Listing';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import useShowFieldVisibleInfo from 'hooks/useShowFieldVisibleInfo';

import './ListingPrices.styles.scss';
import './ListingPrices.styles.responsive.scss';

export type ListingPriceRow =
  | 'askingPrice'
  | 'revenue'
  | 'EBITDA'
  | 'averageStockValue';

type ListingPricesProps = {
  className?: string;
  askingPrice: AskingPriceSelect;
  amount: number;
  amountFrom: number;
  amountTo: number;
  revenue: number;
  EBITDA: number;
  averageStockValue: number;
  stockAtValue: StockAtValue;
  currency: Currency;
  amountRequiredVisibleInfo?: RequiredVisibleInfo;
  amountFromRequiredVisibleInfo?: RequiredVisibleInfo;
  amountToRequiredVisibleInfo?: RequiredVisibleInfo;
  revenueRequiredVisibleInfo?: RequiredVisibleInfo;
  EBITDARequiredVisibleInfo?: RequiredVisibleInfo;
  averageStockValueRequiredVisibleInfo?: RequiredVisibleInfo;
  isPreview?: boolean;
  /**
   * Rows to render even when empty, so they stay clickable in the LCW preview.
   * Which rows apply depends on the listing type - urgent listings have no
   * price guide or stock-at-value selector.
   */
  alwaysShowRows?: ListingPriceRow[];
  /**
   * Rows the LCW preview marks as still needed on the public listing. A red
   * border and nothing else - the label already stands in as the placeholder.
   */
  missingRows?: ListingPriceRow[];
};

const ListingPrices: React.FC<ListingPricesProps> = (props) => {
  const {
    className,
    amount,
    amountTo,
    amountFrom,
    askingPrice,
    averageStockValue,
    EBITDA,
    revenue,
    stockAtValue,
    currency,
    amountRequiredVisibleInfo,
    amountFromRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
    revenueRequiredVisibleInfo,
    EBITDARequiredVisibleInfo,
    averageStockValueRequiredVisibleInfo,
    isPreview = false,
    alwaysShowRows,
    missingRows,
  } = props;

  const forceRow = useCallback(
    (row: ListingPriceRow) => !!alwaysShowRows?.includes(row),
    [alwaysShowRows],
  );

  const rowClasses = useCallback(
    (row: ListingPriceRow) =>
      classNames('listing-prices__price', {
        'listing-prices__price--missing': !!missingRows?.includes(row),
      }),
    [missingRows],
  );

  const { currentUser } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const getShowFieldVisibleInfo = useShowFieldVisibleInfo();

  const showAskingPrice = useMemo(
    () =>
      getAskingPrice({
        amount,
        amountFrom,
        amountTo,
        askingPriceSelect: askingPrice,
      }),
    [amount, amountFrom, amountTo, askingPrice],
  );

  const askingPriceVisibleInfo = useMemo(() => {
    if (askingPrice === ASKING_PRICE_SELECT.FIXED_PRICE)
      return amountRequiredVisibleInfo;

    if (askingPrice === ASKING_PRICE_SELECT.PRICE_RANGE)
      return amountFromRequiredVisibleInfo || amountToRequiredVisibleInfo;

    return undefined;
  }, [
    askingPrice,
    amountRequiredVisibleInfo,
    amountFromRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
  ]);

  const {
    isHidden: isAskingPriceHidden,
    reasonToHideContent: askingPriceReason,
  } = getShowFieldVisibleInfo(askingPriceVisibleInfo);

  const { isHidden: isRevenueHidden, reasonToHideContent: revenueReason } =
    getShowFieldVisibleInfo(revenueRequiredVisibleInfo);

  const { isHidden: isEBITDAHidden, reasonToHideContent: EBITDAReason } =
    getShowFieldVisibleInfo(EBITDARequiredVisibleInfo);

  const {
    isHidden: isAverageStockValueHidden,
    reasonToHideContent: averageStockValueReason,
  } = getShowFieldVisibleInfo(averageStockValueRequiredVisibleInfo);

  const hasEBITDAValue = !!EBITDA && EBITDA > 0;
  const hasStockValue =
    !!averageStockValue && stockAtValue !== STOCK_AT_VALUE.NO_STOCK_AT_VALUE;

  const showAskingPriceRow =
    forceRow('askingPrice') ||
    (!!askingPrice &&
      !!currentUser &&
      (!!showAskingPrice || isAskingPriceHidden));
  const showRevenueRow = forceRow('revenue') || !!revenue || isRevenueHidden;
  const showEBITDARow = forceRow('EBITDA') || hasEBITDAValue || isEBITDAHidden;
  const showAverageStockValueRow =
    forceRow('averageStockValue') ||
    (stockAtValue !== STOCK_AT_VALUE.NO_STOCK_AT_VALUE &&
      (hasStockValue || isAverageStockValueHidden));

  const classes = classNames('listing-prices', className);

  const askingPriceLabel = currency
    ? t('priceGuideWithCurrency', { currency: t(currency) })
    : t('priceGuide');

  const stockLabel =
    stockAtValue === STOCK_AT_VALUE.NO_STOCK_AT_VALUE
      ? t('noStockAtValue')
      : t(
          `stockAtValuation${
            stockAtValue === STOCK_AT_VALUE.INCLUDE_STOCK_AT_VALUE
              ? 'Included'
              : 'Excluded'
          }`,
        );

  /**
   * A row only renders without a value when it was forced (LCW preview), where
   * the label stands in for the missing value in a muted state.
   */
  const emptyValue = useCallback(
    (label: string) => (
      <span className="listing-prices__placeholder">{label}</span>
    ),
    [],
  );

  const infoPopup = useMemo(
    () => (
      <ResolvePopup
        onHover
        popupContent={<span>{t('pricePopupContent')}</span>}
      >
        <InformationcircleIcon />
      </ResolvePopup>
    ),
    [t],
  );

  return (
    <div className={classes}>
      {showAskingPriceRow && (
        <div className={rowClasses('askingPrice')}>
          <span>
            {isPreview && isAskingPriceHidden
              ? askingPriceReason || emptyValue(askingPriceLabel)
              : showAskingPrice ||
                askingPriceReason ||
                emptyValue(askingPriceLabel)}
          </span>
          <span>
            {askingPriceLabel}
            {!showRevenueRow &&
              !showEBITDARow &&
              !showAverageStockValueRow &&
              infoPopup}
          </span>
        </div>
      )}
      {showRevenueRow && (
        <div className={rowClasses('revenue')}>
          <span>
            {revenue && !(isPreview && isRevenueHidden)
              ? mapValueToMoneyFormat(revenue)
              : revenueReason || emptyValue(t('revenue'))}
          </span>
          <span>
            {t('revenue')}
            {!showEBITDARow && !showAverageStockValueRow && infoPopup}
          </span>
        </div>
      )}
      {showEBITDARow && (
        <div className={rowClasses('EBITDA')}>
          <span>
            {hasEBITDAValue && !(isPreview && isEBITDAHidden)
              ? mapValueToEbitdaFormat(EBITDA)
              : EBITDAReason || emptyValue(t('ebitda'))}
          </span>
          <span>
            {t('ebitda')}
            {!showAverageStockValueRow && infoPopup}
          </span>
        </div>
      )}
      {showAverageStockValueRow && (
        <div className={rowClasses('averageStockValue')}>
          <span>
            {hasStockValue && !(isPreview && isAverageStockValueHidden)
              ? mapValueToMoneyFormat(averageStockValue)
              : averageStockValueReason || emptyValue(stockLabel)}
          </span>
          <span>
            {stockLabel}
            {infoPopup}
          </span>
        </div>
      )}
    </div>
  );
};

export default ListingPrices;
